<template>
  <div class="stock-notify">

    <!-- Trigger knop -->
    <button
      @click.stop="modalOpen = true"
      :class="compact
        ? 'flex items-center justify-center gap-1.5 w-full py-2 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] text-[0.78rem] font-semibold text-[#555] hover:border-[#FFD500] hover:bg-[#fffbeb] hover:text-[#111] sn-trigger'
        : 'flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-[#e5e7eb] bg-white text-[0.9rem] font-bold text-[#333] hover:border-[#FFD500] hover:bg-[#fffbeb] sn-trigger'"
    >
      <i class="bi bi-bell text-[#FFD500]"></i>
      {{ compact ? 'Mail mij bij voorraad' : 'Mail mij als het weer op voorraad is' }}
    </button>

    <!-- Modal (via teleport) -->
    <teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="modalOpen"
          class="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50"
          @click.self="closeModal"
        >
          <div ref="modalEl" class="modal-sheet bg-white w-full sm:max-w-[440px] sm:rounded-2xl overflow-hidden shadow-2xl"
               role="dialog" aria-modal="true" aria-label="Houd me op de hoogte">

            <!-- Header -->
            <div class="flex items-center justify-between px-5 py-4 border-b border-[#f0f0f0]">
              <div class="flex items-center gap-2.5">
                <div class="w-8 h-8 bg-[#FFD500] rounded-xl flex items-center justify-center shrink-0">
                  <i class="bi bi-bell-fill text-[#111] text-[0.8rem]"></i>
                </div>
                <span class="font-bold text-[0.9rem] text-[#111]">Melding bij voorraad</span>
              </div>
              <button @click="closeModal" aria-label="Sluiten" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f4f4f4] text-[#888] hover:text-[#111] transition-colors">
                <i class="bi bi-x text-[1.25rem]" aria-hidden="true"></i>
              </button>
            </div>

            <!-- Product mini-summary -->
            <div class="flex items-center gap-3 px-5 py-4 bg-[#fafafa] border-b border-[#f0f0f0]">
              <div class="w-14 h-14 rounded-xl border border-[#e5e7eb] bg-white flex items-center justify-center shrink-0 overflow-hidden p-1">
                <img
                  v-if="productImage"
                  :src="productImage"
                  :alt="productName"
                  class="w-full h-full object-contain"
                >
                <i v-else class="bi bi-box-seam text-[1.4rem] text-[#ccc]"></i>
              </div>
              <div class="min-w-0 flex-1">
                <span class="inline-flex items-center gap-1 text-[0.7rem] font-bold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full mb-1.5">
                  <i class="bi bi-hourglass-split text-[0.65rem]"></i>
                  Tijdelijk uitverkocht
                </span>
                <p class="text-[0.875rem] font-semibold text-[#111] m-0 leading-snug line-clamp-2">{{ productName }}</p>
              </div>
            </div>

            <!-- Form of succes -->
            <div class="px-5 py-5">

              <!-- Succes -->
              <transition name="notify-slide">
                <div v-if="done" class="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-4">
                  <div class="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <i class="bi bi-check2 text-white text-[0.85rem] font-black"></i>
                  </div>
                  <div>
                    <p class="font-bold text-[0.9rem] text-green-800 m-0 mb-0.5">Je bent aangemeld!</p>
                    <p class="text-[0.8rem] text-green-700 m-0">
                      We sturen een bericht naar <strong>{{ email }}</strong> zodra dit product weer beschikbaar is.
                    </p>
                  </div>
                </div>
              </transition>

              <!-- Formulier -->
              <template v-if="!done">
                <p class="text-[0.82rem] text-[#666] mb-4 leading-relaxed">
                  Laat je e-mailadres achter. Zodra dit product weer op voorraad is, sturen we je direct een bericht.
                </p>

                <form @submit.prevent="submit" class="flex gap-2">
                  <input v-model="hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="hidden" />
                  <div class="flex-1 relative">
                    <i class="bi bi-envelope absolute left-3 top-1/2 -translate-y-1/2 text-[#bbb] text-[0.85rem]"></i>
                    <input
                      v-model="email"
                      type="email"
                      placeholder="jouw@email.nl"
                      required
                      :disabled="loading"
                      class="w-full pl-9 pr-3 py-2.5 border border-[#e5e7eb] rounded-xl text-[0.875rem] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-colors disabled:opacity-60"
                    >
                  </div>
                  <button
                    type="submit"
                    :disabled="loading || !email"
                    class="px-4 py-2.5 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold text-[0.875rem] rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0"
                  >
                    <i :class="['bi', loading ? 'bi-arrow-repeat spin' : 'bi-check2']"></i>
                    {{ loading ? '' : 'Aanmelden' }}
                  </button>
                </form>

                <p v-if="errorMsg" class="mt-2 text-[0.78rem] text-red-500 flex items-center gap-1">
                  <i class="bi bi-exclamation-circle"></i> {{ errorMsg }}
                </p>

                <p class="mt-3 text-[0.72rem] text-[#bbb] flex items-center gap-1">
                  <i class="bi bi-shield-check text-[0.75rem]"></i>
                  Je gegevens worden alleen gebruikt voor deze melding.
                </p>
              </template>

            </div>

          </div>
        </div>
      </transition>
    </teleport>

  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAnalytics } from '@composables/useAnalytics'
import { useEscToClose } from '@composables/useEscToClose'
import { useFocusTrap } from '@composables/useFocusTrap'

const { trackStockNotify } = useAnalytics()

const props = defineProps({
  productId:    { type: [Number, String], required: true },
  productName:  { type: String, required: true },
  productSlug:  { type: String, default: '' },
  productImage: { type: String, default: '' },
  compact:      { type: Boolean, default: false },
})

const modalOpen = ref(false)
const done      = ref(false)
const loading   = ref(false)
const email     = ref('')
const hp        = ref('')   // honeypot
const errorMsg  = ref('')

const modalEl = ref(null)
const closeModal = () => {
  if (!loading.value) modalOpen.value = false
}
useEscToClose(modalOpen, closeModal)
useFocusTrap(modalOpen, modalEl)

const submit = async () => {
  errorMsg.value = ''
  loading.value  = true
  try {
    const res = await fetch('/api/notify-stock', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email:       email.value,
        productId:   props.productId,
        productName: props.productName,
        productSlug: props.productSlug,
        website:     hp.value,
      }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Er ging iets mis')
    done.value = true
    trackStockNotify(props.productName, props.productId)
    // Na 2.5s modal sluiten
    setTimeout(() => { modalOpen.value = false }, 2500)
  } catch (e) {
    errorMsg.value = e.message || 'Er ging iets mis, probeer opnieuw.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.sn-trigger {
  transition: border-color 150ms cubic-bezier(0.23, 1, 0.32, 1),
              background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              color 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
@media (prefers-reduced-motion: reduce) { .sn-trigger { transition: none; } }

/* ── Modal sheet animatie (slide-up op mobiel) ── */
.modal-sheet {
  border-radius: 20px 20px 0 0;
}
@media (min-width: 640px) {
  .modal-sheet {
    border-radius: 16px;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active .modal-sheet,
.modal-fade-leave-active .modal-sheet {
  transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}
.modal-fade-enter-from .modal-sheet,
.modal-fade-leave-to .modal-sheet {
  transform: translateY(40px);
}
@media (min-width: 640px) {
  .modal-fade-enter-from .modal-sheet,
  .modal-fade-leave-to .modal-sheet {
    transform: scale(0.96) translateY(8px);
  }
}

.notify-slide-enter-active,
.notify-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.notify-slide-enter-from,
.notify-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
